/**
 * Iter 67el + Iter 68d — AI customer-support chat bubble.
 *
 * Mounted on car-detail + checkout pages (the highest-intent moments in
 * the funnel). Floating bottom-right button → expandable panel.
 *
 * Iter 68d additions:
 *   • Proactive popup: after 25s dwell on the page (once per session), the
 *     bubble auto-expands with a friendly "Need help?" invite.
 *   • Lead-capture gate: anonymous visitors must fill name + email +
 *     phone/WhatsApp before they can chat. Logged-in customers bypass
 *     (we already have their info on the user record).
 *   • Captured leads surface in Admin → Operations → Chat Leads with
 *     name/email/phone/message_count/handoff_count.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import useDraggableFab from "@/lib/useDraggableFab";
import {
  MessageCircle, Send, X, MessageSquareWarning, Loader2, Phone,
  Sparkles, UserCircle2, Mail, AtSign, ShieldCheck,
} from "lucide-react";

const SESSION_KEY = "rentalcar:chat_session_id";
const LEAD_OK_KEY = "rentalcar:chat_lead_ok"; // per-session_id sentinel
const PROACTIVE_DELAY_MS = 25_000; // 25s dwell time before auto-popup
const PROACTIVE_OPENED_KEY = "rentalcar:chat_proactive_opened";

/**
 * Fire-and-forget analytics beacon. Never awaited, never throws — a
 * blocked /api/chat/track-event must NEVER break the chat UX.
 */
function _track(sessionId, eventType, extra = {}) {
  try {
    api.post("/chat/track-event", {
      session_id: sessionId,
      event_type: eventType,
      car_id: extra.car_id ?? null,
      locale: extra.locale ?? null,
      question_preview: extra.question_preview ?? null,
    }).catch(() => {});
  } catch {
    /* swallow — analytics must not break the funnel */
  }
}

function _ensureSessionId() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return crypto.randomUUID();
  }
}

// 13-locale strings for the proactive popup invite + lead form. Falls
// back to English for any language outside the supported stack.
const I18N = {
  en: {
    invite_title: "Need help finding your car?",
    invite_body: "Tell us a bit about you and we'll help — usually replies in seconds.",
    invite_cta: "Chat with us",
    invite_dismiss: "No thanks",
    form_title: "Quick intro — then we can chat",
    form_body: "We use this to follow up if the chat drops, or to send your booking via WhatsApp.",
    name: "Full name",
    name_ph: "e.g. Anna Smith",
    email: "Email",
    email_ph: "you@example.com",
    phone: "Phone / WhatsApp",
    phone_ph: "+355 69 …",
    consent: "I'm OK with RentalCar.al following up about my booking.",
    submit: "Start chat",
    privacy: "We never share your info — only used to help with this booking.",
    placeholder: "Ask anything…",
    typing: "Thinking…",
    talk_human: "Talk to {vendor} on WhatsApp",
    title: "RentalCar.al support",
    subtitle: "Usually replies instantly",
  },
  sq: {
    invite_title: "Të ndihmojmë të gjesh makinën?",
    invite_body: "Na trego pak për ty dhe do të ndihmojmë — zakonisht përgjigjemi në sekonda.",
    invite_cta: "Bisedo me ne",
    invite_dismiss: "Jo, faleminderit",
    form_title: "Prezantimi i shkurtër — pastaj bisedojmë",
    form_body: "E përdorim për të të kontaktuar nëse ndërpritet biseda ose për të të dërguar rezervimin në WhatsApp.",
    name: "Emri i plotë",
    name_ph: "p.sh. Anna Hoxha",
    email: "Email",
    email_ph: "ti@shembull.al",
    phone: "Telefon / WhatsApp",
    phone_ph: "+355 69 …",
    consent: "Pranoj që RentalCar.al të më kontaktojë për rezervimin tim.",
    submit: "Fillo bisedën",
    privacy: "Të dhënat janë private — i përdorim vetëm për këtë rezervim.",
    placeholder: "Pyet çfarëdo…",
    typing: "Po mendoj…",
    talk_human: "Fol me {vendor} në WhatsApp",
    title: "Mbështetja RentalCar.al",
    subtitle: "Përgjigjet zakonisht menjëherë",
  },
  it: {
    invite_title: "Hai bisogno di aiuto?",
    invite_body: "Raccontaci un po' di te e ti aiutiamo — di solito rispondiamo in pochi secondi.",
    invite_cta: "Inizia la chat",
    invite_dismiss: "No grazie",
    form_title: "Una breve presentazione — poi possiamo chattare",
    form_body: "La usiamo per ricontattarti se la chat si interrompe o per inviarti la prenotazione su WhatsApp.",
    name: "Nome completo",
    name_ph: "es. Anna Rossi",
    email: "Email",
    email_ph: "tu@esempio.it",
    phone: "Telefono / WhatsApp",
    phone_ph: "+39 333 …",
    consent: "Acconsento al ricontatto di RentalCar.al per la mia prenotazione.",
    submit: "Inizia",
    privacy: "Le tue info restano private — solo per questa prenotazione.",
    placeholder: "Chiedi qualsiasi cosa…",
    typing: "Sto pensando…",
    talk_human: "Parla con {vendor} su WhatsApp",
    title: "Supporto RentalCar.al",
    subtitle: "Risponde subito",
  },
  fr: {
    invite_title: "Besoin d'aide pour trouver votre voiture ?",
    invite_body: "Dites-nous quelques mots et on vous aide — réponse en quelques secondes.",
    invite_cta: "Démarrer la discussion",
    invite_dismiss: "Non merci",
    form_title: "Une rapide présentation — ensuite on discute",
    form_body: "Nous l'utilisons pour vous recontacter si le chat se coupe ou envoyer la réservation par WhatsApp.",
    name: "Nom complet",
    name_ph: "ex. Anna Martin",
    email: "Email",
    email_ph: "vous@exemple.fr",
    phone: "Téléphone / WhatsApp",
    phone_ph: "+33 6 …",
    consent: "J'accepte que RentalCar.al me recontacte au sujet de ma réservation.",
    submit: "Commencer",
    privacy: "Vos infos restent privées — uniquement pour cette réservation.",
    placeholder: "Posez votre question…",
    typing: "Je réfléchis…",
    talk_human: "Parler à {vendor} sur WhatsApp",
    title: "Support RentalCar.al",
    subtitle: "Répond instantanément",
  },
  de: {
    invite_title: "Brauchst du Hilfe bei der Autosuche?",
    invite_body: "Erzähl uns kurz von dir und wir helfen — Antwort meist in Sekunden.",
    invite_cta: "Chat starten",
    invite_dismiss: "Nein danke",
    form_title: "Kurze Vorstellung — dann können wir chatten",
    form_body: "Wir melden uns zurück, falls der Chat abbricht, oder senden die Buchung per WhatsApp.",
    name: "Voller Name",
    name_ph: "z. B. Anna Schmidt",
    email: "E-Mail",
    email_ph: "du@beispiel.de",
    phone: "Telefon / WhatsApp",
    phone_ph: "+49 …",
    consent: "Ich bin einverstanden, dass RentalCar.al mich zur Buchung kontaktiert.",
    submit: "Starten",
    privacy: "Deine Daten bleiben privat — nur für diese Buchung.",
    placeholder: "Stell mir etwas…",
    typing: "Denke nach…",
    talk_human: "Mit {vendor} per WhatsApp sprechen",
    title: "RentalCar.al Support",
    subtitle: "Antwortet sofort",
  },
};

function _strings(locale) {
  const base = (locale || "en").split("-")[0];
  return I18N[base] || I18N.en;
}

export default function CustomerChatBubble({ carId = null, currentUser = null, raised = false }) {
  const fab = useDraggableFab("rentalcar:chat-fab", "right", raised ? 100 : undefined);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [vendorHandoff, setVendorHandoff] = useState(null);
  const [handoffError, setHandoffError] = useState(null);
  // Iter 68d — lead-capture state
  const [showInvitePeek, setShowInvitePeek] = useState(false);
  const [leadOk, setLeadOk] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadError, setLeadError] = useState(null);
  const [leadForm, setLeadForm] = useState({
    name: currentUser?.name || "",
    email: currentUser?.email || "",
    phone: currentUser?.phone || currentUser?.whatsapp || "",
    consent: false,
  });

  const sessionId = useMemo(_ensureSessionId, []);
  const locale = useMemo(() => {
    try { return (navigator.language || "en").slice(0, 5); } catch { return "en"; }
  }, []);
  const t = useMemo(() => _strings(locale), [locale]);
  const scrollerRef = useRef(null);
  // Iter 68g — track the last admin-message timestamp we've ingested so
  // the poll only returns new entries.
  const lastAdminSeenRef = useRef("");
  const [humanTakenOver, setHumanTakenOver] = useState(false);
  const [humanOwnerName, setHumanOwnerName] = useState(null);

  // Logged-in customers bypass the lead-capture form entirely.
  const isAuthed = !!currentUser?.id;

  // Restore "lead already captured" sentinel from sessionStorage so a
  // page reload doesn't ask the visitor again.
  useEffect(() => {
    try {
      const sentinel = sessionStorage.getItem(`${LEAD_OK_KEY}:${sessionId}`);
      if (sentinel === "1" || isAuthed) {
        setLeadOk(true);
      }
    } catch { /* SSR / storage blocked — ignore */ }
  }, [sessionId, isAuthed]);

  // Proactive popup — auto-expand after dwell time on the page. Only
  // fires once per session_id, and skips when the visitor has already
  // opened or dismissed the bubble.
  useEffect(() => {
    let timer;
    try {
      const already = sessionStorage.getItem(`${PROACTIVE_OPENED_KEY}:${sessionId}`);
      if (already || open) return;
      timer = setTimeout(() => {
        try {
          sessionStorage.setItem(`${PROACTIVE_OPENED_KEY}:${sessionId}`, "1");
        } catch { /* ignore */ }
        setShowInvitePeek(true);
        _track(sessionId, "chat_invite_shown", { car_id: carId, locale });
      }, PROACTIVE_DELAY_MS);
    } catch { /* ignore */ }
    return () => clearTimeout(timer);
  }, [sessionId, open, carId, locale]);

  // Seed greeting on first open (localised by detected locale).
  useEffect(() => {
    if (!open) return;
    _track(sessionId, "chat_opened", { car_id: carId, locale });
    if (messages.length) return;
    const base = locale.split("-")[0];
    const greetings = {
      en: "Hi! I'm the RentalCar.al assistant. Ask me anything about your booking, deposits, fuel policy, pickup — or how to find the right car.",
      sq: "Përshëndetje! Unë jam asistenti i RentalCar.al. Më pyet gjithçka për rezervimin, depozitën, karburantin ose marrjen e makinës.",
      it: "Ciao! Sono l'assistente di RentalCar.al. Chiedimi qualsiasi cosa sulla tua prenotazione, deposito, carburante o ritiro auto.",
      fr: "Bonjour ! Je suis l'assistant RentalCar.al. Posez-moi vos questions sur la réservation, la caution, le carburant ou la prise en charge.",
      de: "Hallo! Ich bin der RentalCar.al-Assistent. Frag mich alles zu Buchung, Kaution, Tankregelung oder Abholung.",
      es: "¡Hola! Soy el asistente de RentalCar.al. Pregúntame sobre tu reserva, depósito, combustible o recogida.",
      pt: "Olá! Sou o assistente RentalCar.al. Pergunta-me sobre a tua reserva, depósito, combustível ou levantamento.",
      pl: "Cześć! Jestem asystentem RentalCar.al. Zapytaj o rezerwację, depozyt, paliwo lub odbiór samochodu.",
      ro: "Salut! Sunt asistentul RentalCar.al. Întreabă-mă orice despre rezervare, depozit, combustibil sau preluare.",
      el: "Γεια! Είμαι ο βοηθός RentalCar.al. Ρώτα με για κράτηση, εγγύηση, καύσιμα ή παραλαβή.",
      bg: "Здравей! Аз съм асистентът на RentalCar.al. Питай ме за резервация, депозит, гориво или вземане.",
      tr: "Merhaba! RentalCar.al asistanıyım. Rezervasyon, depozito, yakıt veya teslim alma hakkında her şeyi sor.",
      ru: "Привет! Я ассистент RentalCar.al. Спросите о бронировании, депозите, топливе или получении машины.",
    };
    setMessages([{ role: "assistant", content: greetings[base] || greetings.en }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, sending]);

  // Iter 68g — Poll /chat/messages every 5s while the panel is open
  // AND the user has either captured a lead or is authed. Surfaces
  // admin replies inline with a "Support" badge.
  useEffect(() => {
    if (!open || (!leadOk && !isAuthed)) return;
    let timer;
    let cancelled = false;
    const poll = async () => {
      try {
        const url = lastAdminSeenRef.current
          ? `/chat/messages?session_id=${encodeURIComponent(sessionId)}&since=${encodeURIComponent(lastAdminSeenRef.current)}`
          : `/chat/messages?session_id=${encodeURIComponent(sessionId)}`;
        const { data } = await api.get(url);
        if (cancelled) return;
        if (data?.human_taken_over) {
          setHumanTakenOver(true);
          setHumanOwnerName(data.human_owner_name || null);
        } else if (data?.human_taken_over === false) {
          // Released back to bot — clear local flags.
          setHumanTakenOver(false);
          setHumanOwnerName(null);
        }
        const fresh = Array.isArray(data?.messages) ? data.messages : [];
        if (fresh.length > 0) {
          // Append new admin/system messages and update the watermark.
          setMessages((prev) => [
            ...prev,
            ...fresh.map((m) => ({
              role: m.role === "admin" ? "admin" : "system",
              content: m.content,
              adminName: m.admin_name || null,
            })),
          ]);
          const last = fresh[fresh.length - 1];
          if (last?.at) lastAdminSeenRef.current = last.at;
        }
      } catch {
        /* polling errors are silent */
      }
    };
    // Fire once immediately then every 5s.
    poll();
    timer = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [open, leadOk, isAuthed, sessionId]);

  const submitLead = async (e) => {
    e?.preventDefault?.();
    setLeadError(null);
    const name = leadForm.name.trim();
    const email = leadForm.email.trim();
    const phone = leadForm.phone.trim();
    if (name.length < 2) { setLeadError("Please enter your full name."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setLeadError("Please enter a valid email."); return; }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 6) { setLeadError("Please enter a valid phone or WhatsApp number."); return; }

    setLeadSubmitting(true);
    try {
      await api.post("/chat/lead", {
        session_id: sessionId,
        name,
        email,
        phone,
        locale,
        car_id: carId,
        source_path: typeof window !== "undefined" ? (window.location.pathname + window.location.search).slice(0, 512) : null,
        marketing_consent: !!leadForm.consent,
      });
      try { sessionStorage.setItem(`${LEAD_OK_KEY}:${sessionId}`, "1"); } catch { /* ignore */ }
      setLeadOk(true);
      _track(sessionId, "chat_lead_captured", { car_id: carId, locale });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save your details — please retry.";
      setLeadError(typeof msg === "string" ? msg : "Couldn't save your details — please retry.");
    } finally {
      setLeadSubmitting(false);
    }
  };

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    _track(sessionId, "message_sent", { car_id: carId, locale, question_preview: trimmed });
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", {
        session_id: sessionId,
        message: trimmed,
        locale,
        car_id: carId,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (data.vendor_whatsapp) {
        setVendorHandoff({ vendor_name: data.vendor_name, vendor_id: data.vendor_id });
      }
    } catch (e) {
      // Backend may flip into "lead_required" if the session was cleared
      // (e.g. lead doc deleted by admin). Surface the form again.
      if (e?.response?.status === 412 && e?.response?.data?.detail === "lead_required") {
        try { sessionStorage.removeItem(`${LEAD_OK_KEY}:${sessionId}`); } catch { /* ignore */ }
        setLeadOk(false);
      } else {
        const errMsg = e?.response?.data?.detail || "Sorry — I couldn't reach the assistant. Try tapping 'Talk to a human' below.";
        setMessages((prev) => [...prev, { role: "assistant", content: errMsg, isError: true }]);
      }
    } finally {
      setSending(false);
    }
  };

  const triggerHandoff = async () => {
    setHandoffError(null);
    _track(sessionId, "handoff_clicked", { car_id: carId, locale });
    try {
      const lastUser = [...messages].reverse().find((m) => m.role === "user");
      const { data } = await api.post("/chat/handoff", {
        session_id: sessionId,
        car_id: carId,
        channel: "whatsapp",
        summary: (lastUser?.content || "").slice(0, 500),
      });
      window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setHandoffError(e?.response?.data?.detail || "Could not open WhatsApp.");
    }
  };

  const dismissInvite = () => {
    setShowInvitePeek(false);
    _track(sessionId, "chat_invite_dismissed", { car_id: carId, locale });
  };

  const acceptInvite = () => {
    setShowInvitePeek(false);
    setOpen(true);
  };

  // Lead form is shown when the visitor isn't authed AND hasn't captured.
  const needsLead = !isAuthed && !leadOk;

  return (
    <>
      {/* Proactive invite peek — small card anchored bottom-right above
          the bubble. Auto-dismisses on outside click. Stays out of the
          way (z-50, max-width 320px) so it doesn't fight CTAs. */}
      {!open && showInvitePeek && (
        <div
          className={`fixed ${raised ? "bottom-[11.5rem] lg:bottom-24" : "bottom-[calc(env(safe-area-inset-bottom)+6.5rem)] lg:bottom-24"} right-5 z-50 w-[320px] max-w-[calc(100vw-2.5rem)] rounded-2xl bg-white shadow-2xl ring-1 ring-amber-200 overflow-hidden animate-in slide-in-from-bottom-4 fade-in-0 duration-300`}
          role="dialog"
          aria-label="Chat invite"
          data-testid="chat-invite-peek"
        >
          <div className="bg-gradient-to-br from-amber-100 to-amber-50 px-4 py-3 flex items-start gap-2">
            <div className="h-9 w-9 rounded-full bg-amber-500 text-amber-950 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-semibold text-amber-950">{t.invite_title}</div>
              <p className="text-[12px] text-amber-900/80 mt-1 leading-relaxed">{t.invite_body}</p>
            </div>
            <button
              type="button"
              onClick={dismissInvite}
              className="h-6 w-6 rounded-full hover:bg-white/60 text-amber-900 flex items-center justify-center shrink-0 -mr-1 -mt-1"
              aria-label="Dismiss"
              data-testid="chat-invite-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 bg-white">
            <button
              type="button"
              onClick={acceptInvite}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950 text-xs font-semibold transition"
              data-testid="chat-invite-cta"
            >
              <MessageCircle className="h-3.5 w-3.5" /> {t.invite_cta}
            </button>
            <button
              type="button"
              onClick={dismissInvite}
              className="text-[11px] text-neutral-500 hover:text-neutral-700 px-2 py-1"
            >
              {t.invite_dismiss}
            </button>
          </div>
        </div>
      )}

      {/* Floating button — bottom right, always above content. */}
      {!open && (
        <button
          type="button"
          style={fab.style}
          onPointerDown={fab.onPointerDown}
          onClick={() => { if (!fab.wasDragged()) setOpen(true); }}
          className={`z-50 h-14 w-14 rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950 shadow-2xl shadow-amber-500/30 flex items-center justify-center transition-colors select-none ${showInvitePeek ? "animate-pulse" : ""}`}
          aria-label="Open support chat (drag to move)"
          data-testid="chat-bubble-open"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed z-50 right-2 left-2 sm:left-auto bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] lg:bottom-5 lg:right-5 sm:w-[min(380px,calc(100vw-2.5rem))] h-[min(580px,calc(100dvh-9rem))] lg:h-[min(580px,calc(100vh-2.5rem))] flex flex-col rounded-3xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden"
          data-testid="chat-bubble-panel"
          role="dialog"
          aria-label="Customer support chat"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-amber-950 px-4 py-3 flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-white/80 flex items-center justify-center shrink-0">
              <MessageCircle className="h-4 w-4 text-amber-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-semibold">{t.title}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{t.subtitle}</div>
            </div>
            <button
              type="button"
              onClick={() => { _track(sessionId, "chat_closed", { car_id: carId, locale }); setOpen(false); }}
              className="h-8 w-8 rounded-full hover:bg-white/20 flex items-center justify-center"
              aria-label="Close chat"
              data-testid="chat-bubble-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {needsLead ? (
            // ─────── Lead-capture gate (anonymous visitors only) ──────
            <form
              onSubmit={submitLead}
              className="flex-1 overflow-y-auto px-4 py-5 bg-neutral-50/60 space-y-3"
              data-testid="chat-lead-form"
            >
              <div className="rounded-2xl bg-white border border-neutral-200 px-3 py-3">
                <div className="font-display text-sm font-semibold text-neutral-900">
                  {t.form_title}
                </div>
                <p className="text-[11.5px] text-neutral-500 leading-relaxed mt-1">
                  {t.form_body}
                </p>
              </div>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1">
                  <UserCircle2 className="h-3 w-3" /> {t.name}
                </span>
                <input
                  type="text"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder={t.name_ph}
                  required
                  maxLength={120}
                  className="mt-1 w-full px-3 py-2 rounded-xl bg-white border border-neutral-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 text-sm outline-none"
                  data-testid="chat-lead-name"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1">
                  <Mail className="h-3 w-3" /> {t.email}
                </span>
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm((s) => ({ ...s, email: e.target.value }))}
                  placeholder={t.email_ph}
                  required
                  maxLength={255}
                  className="mt-1 w-full px-3 py-2 rounded-xl bg-white border border-neutral-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 text-sm outline-none"
                  data-testid="chat-lead-email"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1">
                  <AtSign className="h-3 w-3" /> {t.phone}
                </span>
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm((s) => ({ ...s, phone: e.target.value }))}
                  placeholder={t.phone_ph}
                  required
                  maxLength={40}
                  className="mt-1 w-full px-3 py-2 rounded-xl bg-white border border-neutral-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 text-sm outline-none"
                  data-testid="chat-lead-phone"
                />
              </label>
              <label className="flex items-start gap-2 text-[11px] text-neutral-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={leadForm.consent}
                  onChange={(e) => setLeadForm((s) => ({ ...s, consent: e.target.checked }))}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-neutral-300 text-amber-500 focus:ring-amber-200"
                  data-testid="chat-lead-consent"
                />
                <span>{t.consent}</span>
              </label>
              {leadError && (
                <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1.5" data-testid="chat-lead-error">
                  {leadError}
                </div>
              )}
              <button
                type="submit"
                disabled={leadSubmitting}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-amber-500 hover:bg-amber-400 disabled:bg-neutral-200 disabled:text-neutral-500 text-amber-950 text-sm font-semibold transition"
                data-testid="chat-lead-submit"
              >
                {leadSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t.submit}
              </button>
              <p className="text-[10px] text-neutral-400 inline-flex items-center gap-1 leading-relaxed">
                <ShieldCheck className="h-3 w-3 shrink-0" />
                <span>{t.privacy}</span>
              </p>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div
                ref={scrollerRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-neutral-50/60"
                data-testid="chat-bubble-messages"
              >
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`max-w-[85%] ${m.role === "user" ? "ml-auto" : "mr-auto"}`}
                  >
                    {m.role === "admin" && (
                      <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 px-1 mb-0.5 inline-flex items-center gap-1" data-testid="chat-bubble-admin-badge">
                        <ShieldCheck className="h-2.5 w-2.5" /> Support{m.adminName ? ` · ${m.adminName}` : ""}
                      </div>
                    )}
                    <div
                      className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                        m.role === "user"
                          ? "bg-amber-500 text-amber-950 rounded-br-md"
                          : m.role === "admin"
                            ? "bg-emerald-500 text-emerald-950 rounded-bl-md shadow-sm"
                            : m.role === "system"
                              ? "bg-neutral-100 text-neutral-500 text-[11px] italic"
                              : m.isError
                                ? "bg-rose-50 text-rose-900 border border-rose-200 rounded-bl-md"
                                : "bg-white text-neutral-900 rounded-bl-md shadow-sm border border-neutral-100"
                      }`}
                      data-testid={`chat-bubble-msg-${m.role}`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {humanTakenOver && (
                  <div className="text-center text-[10px] text-emerald-700 italic" data-testid="chat-bubble-human-banner">
                    {humanOwnerName ? `${humanOwnerName} is helping you live.` : "A real person is helping you live."}
                  </div>
                )}
                {sending && (
                  <div className="max-w-[85%] mr-auto">
                    <div className="px-3 py-2 rounded-2xl bg-white border border-neutral-100 inline-flex items-center gap-2 text-xs text-neutral-500" data-testid="chat-bubble-typing">
                      <Loader2 className="h-3 w-3 animate-spin" /> {t.typing}
                    </div>
                  </div>
                )}
              </div>

              {/* Handoff CTA — shown once the bot has replied. */}
              {messages.length > 0 && (
                <div className="border-t border-neutral-200 bg-white px-3 py-2">
                  <button
                    type="button"
                    onClick={triggerHandoff}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-semibold transition"
                    data-testid="chat-bubble-handoff"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {t.talk_human.replace("{vendor}", vendorHandoff?.vendor_name || "a human")}
                  </button>
                  {handoffError && (
                    <div className="text-[10px] text-rose-600 mt-1 inline-flex items-center gap-1" data-testid="chat-bubble-handoff-error">
                      <MessageSquareWarning className="h-3 w-3" /> {handoffError}
                    </div>
                  )}
                </div>
              )}

              {/* Input */}
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="border-t border-neutral-200 bg-white p-3 flex items-center gap-2"
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t.placeholder}
                  className="flex-1 px-3 py-2 rounded-full bg-neutral-100 border-0 focus:bg-white focus:ring-2 focus:ring-amber-300 text-sm outline-none"
                  disabled={sending}
                  data-testid="chat-bubble-input"
                  maxLength={1500}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="h-9 w-9 rounded-full bg-amber-500 hover:bg-amber-400 disabled:bg-neutral-200 text-amber-950 flex items-center justify-center transition shrink-0"
                  aria-label="Send"
                  data-testid="chat-bubble-send"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
